import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { customersAPI, inquiriesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  ArrowLeft, FileText, ShoppingCart, MessageSquare, Package, Activity,
  TrendingUp, IndianRupee, Clock, Target, Search, MessageSquarePlus, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtMoney = (n, ccy = 'INR') => {
  if (n === null || n === undefined) return '-';
  const sym = ccy === 'INR' ? '₹' : ccy === 'USD' ? '$' : ccy === 'EUR' ? '€' : ccy + ' ';
  return `${sym}${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const fmtDate = (s) => {
  if (!s) return '-';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const statusColor = (s) => {
  const x = String(s || '').toLowerCase();
  if (['approved', 'won', 'released', 'confirmed', 'delivered'].includes(x)) return 'bg-emerald-100 text-emerald-700';
  if (['rejected', 'lost', 'cancelled'].includes(x)) return 'bg-red-100 text-red-700';
  if (['draft', 'pending'].includes(x)) return 'bg-slate-100 text-slate-700';
  return 'bg-blue-100 text-blue-700';
};

function KpiCard({ label, value, sub, icon: Icon, accent = 'text-primary' }) {
  return (
    <Card data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="py-4 px-5">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
          {Icon && <Icon className={`h-4 w-4 ${accent}`} />}
        </div>
        <p className={`text-2xl font-bold font-mono ${accent}`}>{value}</p>
        {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function TimelineRow({ entry }) {
  const icon = entry.type === 'quotation' ? FileText
    : entry.type === 'order' ? ShoppingCart
    : MessageSquare;
  const Icon = icon;
  const targetUrl = entry.type === 'quotation' ? `/quotations/${entry.id}`
    : entry.type === 'order' ? `/orders/${entry.id}`
    : `/inquiries/${entry.id}`;
  return (
    <Link to={targetUrl}
      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/60 transition border border-transparent hover:border-border"
      data-testid={`timeline-${entry.type}-${entry.id}`}>
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground">{entry.type}</span>
          <span className="font-mono font-medium">{entry.number}</span>
          {entry.status && <Badge className={statusColor(entry.status)} variant="outline">{entry.status}</Badge>}
        </div>
        {entry.subject && <p className="text-sm text-muted-foreground truncate">{entry.subject}</p>}
      </div>
      {entry.amount != null && (
        <p className="font-mono text-sm shrink-0">{fmtMoney(entry.amount, entry.currency)}</p>
      )}
      <p className="text-xs text-muted-foreground shrink-0 w-24 text-right">{fmtDate(entry.at)}</p>
    </Link>
  );
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creatingInquiry, setCreatingInquiry] = useState(false);

  const createInquiry = async () => {
    setCreatingInquiry(true);
    try {
      const response = await inquiriesAPI.create({ customer_id: id, products: [] });
      toast.success(`Inquiry ${response.data.inquiry_number} created`, {
        description: 'Attach the customer document inside the inquiry and run AI extraction.',
      });
      navigate(`/inquiries/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the inquiry');
    } finally {
      setCreatingInquiry(false);
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    customersAPI.getHistory(id)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { toast.error('Failed to load customer history'); navigate('/customers'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, navigate]);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }
  if (!data) return null;

  const { customer, kpis, timeline, quotations, orders, inquiries, parts_quoted } = data;
  const filterText = search.trim().toLowerCase();
  const filt = (arr, fields) => !filterText ? arr
    : arr.filter((r) => fields.some((f) => String(r[f] || '').toLowerCase().includes(filterText)));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate('/customers')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">{customer.name}</h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
              <span className="font-mono">{customer.customer_code}</span>
              <Badge className={customer.customer_type === 'export' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                {customer.customer_type === 'export' ? 'Export' : 'Domestic'}
              </Badge>
              <span>{customer.country || 'India'}</span>
              {customer.currency && <Badge variant="outline">{customer.currency}</Badge>}
            </div>
          </div>
        </div>
        <Button onClick={createInquiry} disabled={creatingInquiry} data-testid="new-inquiry-from-customer-btn">
          {creatingInquiry ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageSquarePlus className="mr-2 h-4 w-4" />}
          New Inquiry
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Quotations" value={kpis.total_quotations} sub={`${kpis.pending} pending`} icon={FileText} accent="text-blue-600" />
        <KpiCard label="Win Rate" value={`${kpis.win_rate}%`} sub={`${kpis.approved} approved`} icon={Target} accent="text-emerald-600" />
        <KpiCard label="Orders" value={kpis.total_orders} sub="lifetime" icon={ShoppingCart} accent="text-amber-600" />
        <KpiCard label="Revenue" value={fmtMoney(kpis.total_revenue, customer.currency)} sub="from SOs" icon={IndianRupee} accent="text-purple-600" />
        <KpiCard label="Avg Conversion" value={kpis.avg_conversion_days != null ? `${kpis.avg_conversion_days}d` : '-'} sub="quote → order" icon={Clock} accent="text-cyan-600" />
        <KpiCard label="Unique Parts" value={kpis.unique_parts_quoted} sub="ever quoted" icon={Package} accent="text-pink-600" />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="timeline" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-5 gap-1 w-full md:max-w-3xl">
          <TabsTrigger value="timeline" data-testid="tab-timeline"><Activity className="h-4 w-4 mr-1.5" />Timeline</TabsTrigger>
          <TabsTrigger value="quotations" data-testid="tab-quotations"><FileText className="h-4 w-4 mr-1.5" />Quotations ({quotations.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders"><ShoppingCart className="h-4 w-4 mr-1.5" />Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries"><MessageSquare className="h-4 w-4 mr-1.5" />RFQs ({inquiries.length})</TabsTrigger>
          <TabsTrigger value="parts" data-testid="tab-parts"><Package className="h-4 w-4 mr-1.5" />Parts ({parts_quoted.length})</TabsTrigger>
        </TabsList>

        <div className="mt-3 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by number, status, PO…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="customer-search-input"
            />
          </div>
        </div>

        {/* Timeline */}
        <TabsContent value="timeline" className="mt-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base uppercase tracking-wide">Activity Timeline</CardTitle></CardHeader>
            <CardContent>
              {timeline.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No activity yet</p>
              ) : (
                <div className="space-y-1 max-h-[640px] overflow-y-auto pr-2">
                  {filt(timeline, ['number', 'status', 'subject', 'ref']).map((t) => (
                    <TimelineRow key={`${t.type}-${t.id}`} entry={t} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quotations */}
        <TabsContent value="quotations" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Validity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filt(quotations, ['quotation_number', 'status']).map((q) => (
                  <TableRow key={q.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/quotations/${q.id}`)}>
                    <TableCell className="font-mono font-medium">{q.quotation_number}{q.version_number > 1 && <span className="text-muted-foreground ml-1">v{q.version_number}</span>}</TableCell>
                    <TableCell>{fmtDate(q.created_at)}</TableCell>
                    <TableCell>{fmtDate(q.validity_date)}</TableCell>
                    <TableCell><Badge className={statusColor(q.status)} variant="outline">{q.status}</Badge></TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(q.total_amount || q.subtotal, q.currency)}</TableCell>
                  </TableRow>
                ))}
                {filt(quotations, ['quotation_number', 'status']).length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No quotations</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* Orders */}
        <TabsContent value="orders" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Customer PO</TableHead>
                  <TableHead>Quotation</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filt(orders, ['order_number', 'customer_po_number', 'quotation_number', 'status']).map((o) => (
                  <TableRow key={o.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/orders/${o.id}`)}>
                    <TableCell className="font-mono font-medium">{o.order_number}</TableCell>
                    <TableCell className="font-mono text-sm">{o.customer_po_number || '-'}</TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{o.quotation_number || '-'}</TableCell>
                    <TableCell>{fmtDate(o.created_at)}</TableCell>
                    <TableCell><Badge className={statusColor(o.status)} variant="outline">{o.status}</Badge></TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(o.total_amount || o.subtotal, o.currency)}</TableCell>
                  </TableRow>
                ))}
                {filt(orders, ['order_number', 'customer_po_number', 'quotation_number', 'status']).length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No orders</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* RFQs */}
        <TabsContent value="inquiries" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Number</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {filt(inquiries, ['inquiry_number', 'subject', 'status']).map((inq) => (
                  <TableRow key={inq.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/inquiries/${inq.id}`)}>
                    <TableCell className="font-mono font-medium">{inq.inquiry_number}</TableCell>
                    <TableCell className="max-w-md truncate">{inq.subject || '-'}</TableCell>
                    <TableCell><Badge className={statusColor(inq.status)} variant="outline">{inq.status}</Badge></TableCell>
                    <TableCell>{fmtDate(inq.created_at)}</TableCell>
                  </TableRow>
                ))}
                {filt(inquiries, ['inquiry_number', 'subject', 'status']).length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No inquiries</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* Parts ever quoted */}
        <TabsContent value="parts" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Internal P/N</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Quoted</TableHead>
                  <TableHead className="text-right">Ordered</TableHead>
                  <TableHead>First Quoted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filt(parts_quoted, ['part_internal_no', 'description']).map((p) => (
                  <TableRow key={p.part_id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/parts/${p.part_id}`)}>
                    <TableCell className="font-mono font-medium">{p.part_internal_no || '-'}</TableCell>
                    <TableCell className="max-w-md truncate">{p.description || '-'}</TableCell>
                    <TableCell className="text-right font-mono">{p.quote_count}</TableCell>
                    <TableCell className="text-right font-mono">{p.order_count}</TableCell>
                    <TableCell>{fmtDate(p.first_quoted_at)}</TableCell>
                  </TableRow>
                ))}
                {filt(parts_quoted, ['part_internal_no', 'description']).length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No parts quoted</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
